import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, LogOut, CalendarDays, Users, ChevronDown, ChevronUp, Mail, Phone, Newspaper, Briefcase, BarChart3, UserCog, ScrollText, Shield, ShieldCheck, MailQuestion, Image as ImageIcon, Type, Loader2 } from "lucide-react";
import AdminNewsletterTab from "@/components/AdminNewsletterTab";
import AdminJobsTab from "@/components/AdminJobsTab";
import AdminChartsTab from "@/components/AdminChartsTab";
import AdminUsersTab from "@/components/AdminUsersTab";
import AdminAuditTab from "@/components/AdminAuditTab";
import AdminContactTab from "@/components/AdminContactTab";
import AdminMediaTab from "@/components/AdminMediaTab";
import AdminTextsTab from "@/components/AdminTextsTab";
const AdminEventsTab = lazy(() => import("@/components/AdminEventsTab"));
import TwoFactorDialog from "@/components/TwoFactorDialog";
import NotifyEmailField from "@/components/NotifyEmailField";
import Mandatory2faSetup from "@/components/Mandatory2faSetup";
import LastActivityBadge from "@/components/LastActivityBadge";
import PageViewsCard from "@/components/PageViewsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "events", label: "Veranstaltungen", Icon: CalendarDays },
  { key: "newsletter", label: "Newsletter", Icon: Newspaper },
  { key: "jobs", label: "Karriere", Icon: Briefcase },
  { key: "charts", label: "Charts", Icon: BarChart3 },
  { key: "contact", label: "Kontakt", Icon: MailQuestion },
  { key: "media", label: "Medien", Icon: ImageIcon },
  { key: "texts", label: "Texte", Icon: Type },
  { key: "users", label: "Benutzer", Icon: UserCog },
  { key: "audit", label: "Audit-Log", Icon: ScrollText },
];

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  // 2FA login flow
  const [twofaPending, setTwofaPending] = useState(null); // { tmp_token }
  const [twofaCode, setTwofaCode] = useState("");
  const [twofaRecoveryMode, setTwofaRecoveryMode] = useState(false);
  // 2FA settings dialog
  const [twofaDialogOpen, setTwofaDialogOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "", time: "", location: "", recurring: "none", recurring_end_date: "" });
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [registrations, setRegistrations] = useState({});

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/all`, { withCredentials: true });
      setEvents(data);
    } catch (err) {
      console.error("Events fetch error:", err);
    }
  }, []);

  const fetchRegistrations = useCallback(async (eventId) => {
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/registrations`, { withCredentials: true });
      setRegistrations(prev => ({ ...prev, [eventId]: data }));
    } catch (err) {
      console.error("Registrations fetch error:", err);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { if (user) fetchEvents(); }, [user, fetchEvents]);

  // Derive accessible tabs from user permissions; auto-select first one
  const perms = user?.permissions || [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.key));
  useEffect(() => {
    if (!user) return;
    if (!activeTab || !visibleTabs.find((t) => t.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || null);
    }
  }, [user, activeTab, visibleTabs]);

  const toggleExpand = (eventId) => {
    if (expandedEvent === eventId) {
      setExpandedEvent(null);
    } else {
      setExpandedEvent(eventId);
      if (!registrations[eventId]) fetchRegistrations(eventId);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: loginForm.email, password: loginForm.password },
        { withCredentials: true }
      );
      if (data.requires_2fa) {
        setTwofaPending({ tmp_token: data.tmp_token });
        setTwofaCode("");
      } else {
        setUser(data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login fehlgeschlagen");
    }
  };

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/login`,
        { tmp_token: twofaPending.tmp_token, code: twofaCode },
        { withCredentials: true }
      );
      setTwofaPending(null);
      setTwofaCode("");
      setUser(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Ungültiger Code");
    }
  };

  const cancel2fa = () => {
    setTwofaPending(null);
    setTwofaCode("");
    setTwofaRecoveryMode(false);
    setLoginError("");
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const openCreateDialog = () => {
    setEditing(null);
    setEventForm({ title: "", description: "", date: "", time: "", location: "", recurring: "none", recurring_end_date: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditing(event);
    setEventForm({
      title: event.title,
      description: event.description || "",
      date: event.date,
      time: event.time || "",
      location: event.location || "",
      recurring: "none",
      recurring_end_date: "",
    });
    setDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/events/${editing.id || editing._id}`, eventForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/events`, eventForm, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Save event error:", err);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Veranstaltung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      fetchEvents();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0C1620] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0C1620] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl text-white mb-2">Admin Login</h1>
            <p className="text-[#8899AA] text-sm">
              {twofaPending ? "Zwei-Faktor-Authentifizierung" : "Veranstaltungskalender verwalten"}
            </p>
          </div>
          {twofaPending ? (
            <form onSubmit={handle2faSubmit} className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-8 space-y-5" data-testid="admin-2fa-form">
              <div className="flex items-center justify-center mb-2">
                <div className="w-12 h-12 rounded-full bg-[#C5993A]/15 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#C5993A]" />
                </div>
              </div>
              <p className="text-sm text-[#8899AA] text-center">
                {twofaRecoveryMode
                  ? "Geben Sie einen Ihrer 8-stelligen Recovery-Codes ein (Format XXXX-XXXX)."
                  : "Geben Sie den 6-stelligen Code aus Microsoft Authenticator ein."}
              </p>
              <div>
                <Input
                  data-testid="admin-2fa-code-input"
                  inputMode={twofaRecoveryMode ? "text" : "numeric"}
                  pattern={twofaRecoveryMode ? undefined : "[0-9]*"}
                  maxLength={twofaRecoveryMode ? 12 : 6}
                  required
                  autoFocus
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(twofaRecoveryMode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
                  placeholder={twofaRecoveryMode ? "XXXX-XXXX" : "123456"}
                  className={`bg-[#0C1620] border-[#3A4F60]/50 text-white text-center focus:border-[#C5993A] ${twofaRecoveryMode ? "text-xl tracking-[0.3em] font-mono" : "text-2xl tracking-[0.5em]"}`}
                />
              </div>
              {loginError && <p data-testid="admin-2fa-error" className="text-red-400 text-sm text-center">{loginError}</p>}
              <Button
                data-testid="admin-2fa-submit"
                type="submit"
                disabled={twofaRecoveryMode ? twofaCode.replace(/[^A-Z0-9]/g, "").length < 8 : twofaCode.length !== 6}
                className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
              >
                Bestätigen
              </Button>
              <button
                type="button"
                data-testid="admin-2fa-toggle-recovery"
                onClick={() => { setTwofaRecoveryMode((v) => !v); setTwofaCode(""); setLoginError(""); }}
                className="block w-full text-[#C5993A] hover:underline text-sm text-center"
              >
                {twofaRecoveryMode ? "Authenticator-Code verwenden" : "Recovery-Code verwenden"}
              </button>
              <button
                type="button"
                onClick={cancel2fa}
                data-testid="admin-2fa-cancel"
                className="block w-full text-[#8899AA] hover:text-[#C5993A] text-sm text-center transition-colors"
              >
                Zurück zur Anmeldung
              </button>
            </form>
          ) : (
          <form onSubmit={handleLogin} className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">E-Mail</label>
              <Input data-testid="admin-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
            </div>
            <div>
              <label className="block text-sm text-[#8899AA] mb-1.5">Passwort</label>
              <Input data-testid="admin-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A]" />
            </div>
            {loginError && <p data-testid="admin-login-error" className="text-red-400 text-sm">{loginError}</p>}
            <Button data-testid="admin-login-button" type="submit" className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">Anmelden</Button>
          </form>
          )}
          <div className="text-center mt-6">
            <Link to="/" className="text-[#8899AA] hover:text-[#C5993A] text-sm transition-colors">
              <ArrowLeft className="inline w-4 h-4 mr-1" /> Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Mandatory 2FA enforcement — block dashboard until enabled
  if (!user.totp_enabled) {
    return <Mandatory2faSetup user={user} onCompleted={checkAuth} onLogout={handleLogout} />;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0C1620]">
      <header className="bg-[#0C1620]/80 backdrop-blur-xl border-b border-[#3A4F60]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/" className="text-[#8899AA] hover:text-[#C5993A] transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
            <h1 className="font-heading text-lg sm:text-xl text-white">Admin</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <LastActivityBadge at={user.previous_login_at} ip={user.previous_login_ip} />
            <Button
              data-testid="admin-2fa-settings-button"
              onClick={() => setTwofaDialogOpen(true)}
              variant="ghost"
              className="text-[#8899AA] hover:text-[#C5993A] text-sm"
              title="Zwei-Faktor-Authentifizierung"
            >
              {user.totp_enabled ? <ShieldCheck className="w-4 h-4 sm:mr-2 text-green-400" /> : <Shield className="w-4 h-4 sm:mr-2" />}
              <span className="hidden sm:inline">2FA</span>
            </Button>
            <Button data-testid="admin-logout-button" onClick={handleLogout} variant="ghost" className="text-[#8899AA] hover:text-[#C5993A] text-sm">
              <LogOut className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Abmelden</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <PageViewsCard />
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#3A4F60]/30 pb-4">
          {visibleTabs.map((t) => {
            const Icon = t.Icon;
            const active = activeTab === t.key;
            return (
              <Button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                variant={active ? "default" : "ghost"}
                className={active ? "bg-[#C5993A] text-white border-0" : "text-[#8899AA]"}
              >
                <Icon className="w-4 h-4 mr-2" /> {t.label}
              </Button>
            );
          })}
        </div>

        {visibleTabs.length === 0 ? (
          <div className="text-center py-16 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg">
            <p className="text-[#8899AA]">Sie haben keine Berechtigung für Admin-Tabs. Bitte kontaktieren Sie einen Administrator.</p>
          </div>
        ) : activeTab === "newsletter" ? (
          <AdminNewsletterTab />
        ) : activeTab === "jobs" ? (
          <AdminJobsTab />
        ) : activeTab === "charts" ? (
          <AdminChartsTab />
        ) : activeTab === "users" ? (
          <AdminUsersTab currentUser={user} onUserUpdated={checkAuth} />
        ) : activeTab === "contact" ? (
          <AdminContactTab />
        ) : activeTab === "media" ? (
          <AdminMediaTab />
        ) : activeTab === "texts" ? (
          <AdminTextsTab />
        ) : activeTab === "audit" ? (
          <AdminAuditTab />
        ) : activeTab === "events" ? (
          <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C5993A]" /></div>}>
            <AdminEventsTab />
          </Suspense>
        ) : null}
      </main>

      <TwoFactorDialog
        open={twofaDialogOpen}
        onClose={() => setTwofaDialogOpen(false)}
        user={user}
        onChanged={checkAuth}
      />
    </div>
  );
}
